import { API_CALL, SET_TOUR_V2_SELECTED_PACKAGE_TYPE } from '../actionConstants'
import {
  FETCH_TAILOR_MADE_TOUR,
  FETCH_TAILOR_MADE_TOUR_ORDER,
  FETCH_TOUR_OPERATORS,
  FETCH_TOURV2_OFFER,
  FETCH_TOURV2_SNAPSHOT_DETAILS, SEND_WAITLIST_REQUEST,
  FETCH_TOUR_DEALS,
  SET_TOUR_PRICE_MODE,
  FETCH_TOUR_DEAL,
  FETCH_TOUR_DEAL_DETAILS,
  FETCH_TOUR_DEAL_FACETS,
} from '../apiActionConstants'
import { getSnapshotDetailsForTourV2Offer, getTourOperators, requestGetTailorMadeTour, requestGetTailorMadeTourOrder, sendWaitingListRequestForTourV2, getTourDeals, getTourDeal, getTourDealDetails, getTourDealFacets } from 'api/tour/tourV2'
import * as OfferService from 'api/offer'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { AppAction } from '../types/ActionTypes'

export function fetchTourV2Offer(offerId: string, privateRequestKey?: string, regionCode?: string, allPackages?: boolean): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    const offerKey = privateRequestKey ? `${offerId}-${privateRequestKey}` : offerId

    if (state.offer.offersLoading[offerKey] ||
      state.offer.tourV2Offers[offerKey] ||
      state.offer.offerErrors[offerKey]) {
      return
    }
    dispatch({
      type: API_CALL,
      api: FETCH_TOURV2_OFFER,
      request: () =>
        OfferService.getOfferById(offerId, {
          region: regionCode || state.geo.currentRegionCode,
          privateRequestKey,
          allPackages,
        }) as Promise<App.Tours.TourV2Offer>,
      offerId: offerKey,
    })
  }
}

export function fetchTourV2SnapshotDetails(reservationId: string): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    if (state.order.tourV2SnapshotsLoading[reservationId] ||
      state.order.tourV2SnapshotDetails[reservationId]) {
      return
    }
    dispatch({
      type: API_CALL,
      api: FETCH_TOURV2_SNAPSHOT_DETAILS,
      request: () =>
        getSnapshotDetailsForTourV2Offer({ reservationId }),
      reservationId,
    })
  }
}

export function fetchTourOperators(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (state.tour.tourOperators.operators) {
      // already have it or are currently fetching it, don't try again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_OPERATORS,
      request: () => getTourOperators(),
    })
  }
}

export function sendWaitListTourSoldOut(requestWaitingListTourData: App.Tours.WaitingListTourRequest) {
  return {
    type: API_CALL,
    api: SEND_WAITLIST_REQUEST,
    request: () => sendWaitingListRequestForTourV2(requestWaitingListTourData)
      .then(() => {
        showSnackbar(
          'We will keep you notified if a spot on this tour becomes available.',
          'success',
          { heading: 'You’ve joined the waitlist' },
        )
      })
      .catch(() => {
        showSnackbar(
          'Please try again later or contact us for assistance.',
          'critical',
          { heading: 'Oops! Something went wrong' },
        )
      }),
  }
}

export function fetchTailorMadeTour(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.tour.tailorMadeTour?.data || state.tour.tailorMadeTour?.fetching) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TAILOR_MADE_TOUR,
      request: () => {
        return requestGetTailorMadeTour({ region: state.geo.currentRegionCode })
      },
    })
  }
}

export function fetchTailorMadeTourOrder(tailorMadeTourRequestId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const order = state.tour.tailorMadeTourOrders[tailorMadeTourRequestId]

    if (order?.data || order?.fetching) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TAILOR_MADE_TOUR_ORDER,
      tailorMadeTourRequestId,
      request: () => {
        return requestGetTailorMadeTourOrder(tailorMadeTourRequestId, { region: state.geo.currentRegionCode })
      },
    })
  }
}

export function fetchTourDeals(region: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { tourDeals, fetchingTourDeals } = state.tour

    if ((tourDeals && Object.values(tourDeals).length)
      || fetchingTourDeals) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DEALS,
      request: () => getTourDeals(region),
    })
  }
}

export function fetchTourDeal(slug: string, region: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { tourDeals, fetchingTourDealBySlugAndFacets } = state.tour

    if (tourDeals?.[slug] || fetchingTourDealBySlugAndFacets?.[slug]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DEAL,
      slug,
      request: () => {
        return getTourDeal(slug, region)
      },
    })
  }
}

export function fetchTourDealFacets(slug: string, region: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { tourDealFacets, fetchingTourDealFacetsBySlug } = state.tour

    if (tourDealFacets?.[slug] || fetchingTourDealFacetsBySlug?.[slug]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DEAL_FACETS,
      slug,
      request: () => {
        // Fetch the initial facets for the tour deal
        // by getting the tour deal details with no filters
        return getTourDealFacets(slug, region)
      },
    })
  }
}

export function fetchTourDealDetails(slug: string, uniqueKey: string, filters: App.Tours.TourDealFiltersApplied, sortParams: App.Tours.TourDealSortState, region: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { tourDealsDetails, fetchingTourDealDetailsBySlug } = state.tour

    if (tourDealsDetails?.[uniqueKey] || fetchingTourDealDetailsBySlug?.[uniqueKey]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DEAL_DETAILS,
      slug: uniqueKey,
      request: () => {
        return getTourDealDetails(slug, filters, sortParams, region)
      },
    })
  }
}

export function setTourPriceMode(tourPriceMode: 'total' | 'perDay'): AppAction {
  return {
    type: SET_TOUR_PRICE_MODE,
    tourPriceMode,
  }
}

export function setTourV2SelectedPackageType(offerId: string, packageType: string | undefined): AppAction {
  return {
    type: SET_TOUR_V2_SELECTED_PACKAGE_TYPE,
    offerId,
    packageType,
  }
}
